import { FC, useEffect, useMemo, useRef, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { DeleteOutline, PlayArrow, Save, Visibility } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { useSnackbar } from "notistack"
import { UserbotCatalog, UserbotTemplates } from "../../interfaces"
import { usePreviewTemplateMutation, useRunTemplateMutation } from "../../store"
import { useUserbotWriteAccess } from "../../hooks"
import { FieldSpecForm } from "../fields/fieldSpecForm"
import { coerceFieldValue, rawFromFieldValue } from "../fields/fieldSpecCoerce"
import { validateFieldValues } from "../fields/fieldSpecValidate"
import { ClientSelectRaw } from "../chains/executorInputs"
import { LaunchTargetFields } from "../common/launchTargetFields"
import {
  LaunchTargetState,
  launchTargetFromDto,
  serializeLaunchTarget,
  validateLaunchTarget,
} from "../common/launchTarget"
import { launchResultMessage, templateResultPath } from "../common/launchResult"
import { getApiErrorMessage } from "@/core/utils"
import { isBlank } from "../common/jsonUtils"
import { launchIdentity } from "../common/launchIdentity"
import {
  InputPreset,
  listPresets,
  savePreset,
  deletePreset,
} from "./inputPresets"

interface IProps {
  template: UserbotTemplates.Template
}

const needsTarget = (kind: UserbotTemplates.TemplateKind) =>
  kind === "chain" || kind === "scheduled"

const isEmptyValue = (spec: UserbotCatalog.FieldSpec, raw: unknown): boolean =>
  spec.type === "array"
    ? !Array.isArray(raw) || raw.length === 0
    : isBlank(raw == null ? undefined : String(raw))

const initialInputs = (
  variables: UserbotTemplates.TemplateVariable[],
): Record<string, unknown> => {
  const inputs: Record<string, unknown> = {}
  for (const v of variables) {
    inputs[v.key] =
      v.default !== undefined
        ? rawFromFieldValue(v, v.default)
        : v.type === "array"
          ? []
          : v.type === "boolean"
            ? "false"
            : ""
  }
  return inputs
}

// Remount on upstream definition changes, including same-ID version/body updates.
export const TemplateRunForm: FC<IProps> = ({ template }) => (
  <TemplateRunFormInner key={launchIdentity(template)} template={template} />
)

const TemplateRunFormInner: FC<IProps> = ({ template }) => {
  const navigate = useNavigate()
  const canWrite = useUserbotWriteAccess()
  const { enqueueSnackbar } = useSnackbar()

  const [inputs, setInputs] = useState<Record<string, unknown>>(() =>
    initialInputs(template.variables),
  )
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [target, setTarget] = useState<LaunchTargetState>({
    executor:
      template.body.role || template.variables.some((v) => v.key === "role")
        ? "role"
        : "client",
  })
  const [preview, setPreview] =
    useState<UserbotTemplates.TemplatePreviewRes | null>(null)
  const [presets, setPresets] = useState<InputPreset[]>(() =>
    listPresets(template.id),
  )
  const [saveDialog, setSaveDialog] = useState(false)
  const [presetName, setPresetName] = useState("")
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  const currentIdentity = launchIdentity({ template, inputs, target })
  const current = useRef(currentIdentity)
  current.current = currentIdentity
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const invalidate = () => {
    setPreview(null)
  }

  const [previewTemplate, { isLoading: isPreviewing }] =
    usePreviewTemplateMutation()
  const [runTemplate, { isLoading: isRunning }] = useRunTemplateMutation()

  const inputDraft = useMemo(() => {
    const out: Record<string, unknown> = {}
    const errors: Record<string, string> = {}
    for (const v of template.variables) {
      const raw = inputs[v.key]
      if (!v.required && isEmptyValue(v, raw)) continue
      try {
        out[v.key] = coerceFieldValue(v, raw)
      } catch {
        errors[v.key] = "Некорректный JSON в параметре"
      }
    }
    return { values: out, errors }
  }, [inputs, template.variables])
  const coercedInputs = inputDraft.values

  const validateInputs = (): boolean => {
    const errs = {
      ...validateFieldValues(template.variables, inputs),
      ...inputDraft.errors,
    }
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const buildTarget = () => {
    if (template.kind === "trigger") return { clientId: target.clientId }
    return serializeLaunchTarget(target)
  }

  const targetErrors = (): string[] => {
    if (template.kind === "trigger")
      return target.clientId ? [] : ["Укажите клиента"]
    const errors = validateLaunchTarget(target)
    if (errors.length) return errors
    // Validate inherited filters too, without sending general defaults over the authored body.
    const authored = { ...template.body, ...preview?.dto }
    const effectiveFilter = target.targetFilter || authored.targetFilter
    if (
      (!effectiveFilter || effectiveFilter === "knownMemberships") &&
      authored.targetFilterParams != null
    )
      errors.push(
        "Без фильтра / knownMemberships не допускает targetFilterParams. Исправьте определение шаблона.",
      )
    const effective = launchTargetFromDto({
      ...authored,
      ...buildTarget(),
      role: target.executor === "role" ? target.role : undefined,
      clientId: target.executor === "client" ? target.clientId : undefined,
    })
    const params = authored.targetFilterParams as
      | { maxAgeDays?: unknown }
      | undefined
    // Only literal inherited ages can be validated here. The backend resolves
    // authored DSL expressions; inputs need not share the target filter's age.
    if (!target.targetFilter && typeof params?.maxAgeDays !== "number")
      delete effective.maxAgeDays
    return [...errors, ...validateLaunchTarget(effective)]
  }

  const handlePreview = async () => {
    if (inFlight.current) return
    if (!validateInputs()) return
    const identity = currentIdentity
    inFlight.current = true
    invalidate()
    setBusy(true)
    try {
      const res = await previewTemplate({
        id: template.id,
        body: { inputs: coercedInputs },
      }).unwrap()
      if (!mounted.current || current.current !== identity) return
      setPreview(res)
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e), { variant: "error" })
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const handlePlay = async () => {
    if (!canWrite || inFlight.current) return
    if (!validateInputs()) return
    const errors = targetErrors()
    if (errors.length) {
      enqueueSnackbar(errors.join("\n"), {
        variant: "warning",
      })
      return
    }
    inFlight.current = true
    try {
      setBusy(true)
      invalidate()
      const res = await runTemplate({
        id: template.id,
        body: { inputs: coercedInputs, ...buildTarget() },
      }).unwrap()
      enqueueSnackbar(
        res.kind === "chain"
          ? launchResultMessage(res.result)
          : res.kind === "scheduled"
            ? "Расписание создано"
            : "Триггер создан",
        { variant: "success" },
      )
      const dest = templateResultPath(res)
      if (dest) navigate(dest)
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e), { variant: "error" })
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const applyPreset = (preset: InputPreset) => {
    const next: Record<string, unknown> = {
      ...initialInputs(template.variables),
    }
    for (const v of template.variables) {
      if (v.key in preset.inputs) {
        next[v.key] = rawFromFieldValue(v, preset.inputs[v.key])
      }
    }
    setInputs(next)
    invalidate()
  }

  const handleSavePreset = () => {
    if (!presetName.trim()) return
    savePreset(template.id, presetName.trim(), coercedInputs)
    setPresets(listPresets(template.id))
    setPresetName("")
    setSaveDialog(false)
  }

  const handleDeletePreset = (id: string) => {
    deletePreset(template.id, id)
    setPresets(listPresets(template.id))
  }

  const previewValidation = preview?.validation

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">{template.name}</Typography>
        <Typography variant="body2">
          ID: {template.id} · Версия: {template.version}
        </Typography>
        {template.description && (
          <Typography variant="body2" color="text.secondary">
            {template.description}
          </Typography>
        )}
      </Paper>

      {presets.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" mb={1}>
            Пресеты ввода
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {presets.map((p) => (
              <Stack
                key={p.id}
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  pl: 1,
                }}
              >
                <Button size="small" onClick={() => applyPreset(p)}>
                  {p.name}
                </Button>
                <Tooltip title="Удалить пресет">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeletePreset(p.id)}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" mb={2}>
          Переменные
        </Typography>
        {template.variables.length === 0 ? (
          <Typography color="text.secondary">
            У шаблона нет переменных.
          </Typography>
        ) : (
          <FieldSpecForm
            specs={template.variables}
            values={inputs}
            onChange={(next) => {
              setInputs(next)
              invalidate()
            }}
            errors={fieldErrors}
          />
        )}
      </Paper>

      {(needsTarget(template.kind) || template.kind === "trigger") && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" mb={1}>
            Исполнитель
          </Typography>
          {needsTarget(template.kind) ? (
            <LaunchTargetFields
              value={target}
              inheritDefaults
              onChange={(next) => {
                setTarget(next)
                invalidate()
              }}
            />
          ) : (
            <ClientSelectRaw
              executor="client"
              clientId={target.clientId ?? ""}
              role=""
              onClientChange={(clientId) => {
                setTarget({ executor: "client", clientId })
                invalidate()
              }}
              onRoleChange={() => undefined}
            />
          )}
        </Paper>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          variant="outlined"
          startIcon={<Visibility />}
          onClick={handlePreview}
          disabled={isPreviewing || busy}
        >
          Preview
        </Button>
        {canWrite ? (
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            onClick={handlePlay}
            disabled={isRunning || busy}
          >
            Play
          </Button>
        ) : (
          <Tooltip title="Нужно право записи (userbot:w)">
            <span>
              <Button variant="contained" startIcon={<PlayArrow />} disabled>
                Play
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      {preview && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="subtitle2">Предпросмотр DTO</Typography>
            <Button
              size="small"
              startIcon={<Save />}
              onClick={() => setSaveDialog(true)}
            >
              Сохранить ввод
            </Button>
          </Stack>

          {previewValidation &&
            (previewValidation.valid ? (
              <Alert severity="success" sx={{ mb: 1 }}>
                Цепочка валидна
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 1 }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {previewValidation.errors.map((iss, i) => (
                    <li key={i}>
                      {iss.stepId ? `[${iss.stepId}] ` : ""}
                      {iss.path ? `${iss.path}: ` : ""}
                      {iss.message}
                    </li>
                  ))}
                </ul>
              </Alert>
            ))}

          <Divider sx={{ mb: 1 }} />
          <TextField
            label="DTO Preview"
            value={JSON.stringify(preview.dto, null, 2)}
            multiline
            fullWidth
            InputProps={{ readOnly: true, style: { fontFamily: "monospace" } }}
          />
        </Paper>
      )}

      <Dialog open={saveDialog} onClose={() => setSaveDialog(false)}>
        <DialogTitle>Сохранить пресет ввода</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <TextField
              label="Название пресета"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              fullWidth
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialog(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleSavePreset}
            disabled={!presetName.trim()}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
